import React from "react";
import { AnalyticsFiltersState } from "./components/AnalyticsFilters";
import { defaultPeriod } from "./format";

export function useAnalyticsFilters(): [
    AnalyticsFiltersState,
    React.Dispatch<React.SetStateAction<AnalyticsFiltersState>>
] {
    const period = defaultPeriod();
    return React.useState<AnalyticsFiltersState>({
        from: period.from,
        to: period.to,
        gameId: undefined,
        take: 20,
        stuckHours: 24,
        granularity: "day",
    });
}

export type AsyncBlockState<T> = {
    loading: boolean;
    error: string | null;
    data: T | null;
};

export function useAsyncBlock<T>(
    loader: () => Promise<{ result: boolean; value?: T }>,
    enabled: boolean,
    deps: React.DependencyList
): [AsyncBlockState<T>, () => void] {
    const [state, setState] = React.useState<AsyncBlockState<T>>({
        loading: false,
        error: null,
        data: null,
    });
    const loadedOnce = React.useRef(false);
    const requestId = React.useRef(0);

    const run = React.useCallback(async () => {
        const id = ++requestId.current;
        setState((s) => ({ ...s, loading: true, error: null }));
        try {
            const result = await loader();
            if (id !== requestId.current) {
                return;
            }
            if (result.result && result.value !== undefined) {
                setState({ loading: false, error: null, data: result.value });
            } else {
                setState({ loading: false, error: "Не удалось загрузить данные", data: null });
            }
        } catch {
            if (id !== requestId.current) {
                return;
            }
            setState({ loading: false, error: "Ошибка запроса", data: null });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    React.useEffect(() => {
        if (!enabled) {
            return;
        }
        if (!loadedOnce.current) {
            loadedOnce.current = true;
        }
        void run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, run]);

    const refresh = React.useCallback(() => {
        void run();
    }, [run]);

    return [state, refresh];
}
